import { useAuth0 } from '@auth0/auth0-react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import useCompanyId from '@/Hooks/UseCompanyId';
import ResourceNotFoundError from '@/Utils/HTTP/Errors/ResourceNotFoundError';
import http from '@/Utils/HTTP/NewHttpUtils';

export type AtsSetupStatus = 'Unknown' | 'Incomplete' | 'FinalSyncPending' | 'Completed';

export interface AtsConnectionResponse {
    id: string;
    atsTool: string;
    setupStatus: AtsSetupStatus;
    connectedAt: string;
}

interface CreateAtsConnectLinkResponse {
    link: string;
}

export interface AtsIntegrationToolAssets {
    logoUrl: string | null;
    iconUrl: string | null;
    iconBlackUrl: string | null;
}

export interface AtsIntegrationTool {
    id: string;
    label: string;
    assets: AtsIntegrationToolAssets;
    connectionGuideUrl: string | null;
}

const atsConnectionPath = (companyId: string): string => `company/${companyId}/ats-integration`;

export const ATS_CONNECTION_POLL_INTERVAL_MS = 5000;

export const isAtsSyncFinished = (connection: AtsConnectionResponse | null | undefined): boolean =>
    connection === null || connection?.setupStatus === 'Completed';

// Supported ATS tools (Kombo `integration_tool` slugs), fetched from Kombo's tool catalog and
// filtered server-side to the ones TalentMesh allows connecting to. One card is rendered per entry.
export const useAtsIntegrationTools = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useSWR(`ats-integration/tools`, async (path) => {
        return http.get<AtsIntegrationTool[]>({
            path,
            accessToken: await getAccessTokenSilently(),
        });
    });
};

/**
 * The company's ATS connection, or null when nothing is connected.
 * Polled while the provider is still provisioning the integration, so a caller watching for the
 * first sync to finish does not have to reload.
 *
 * @param enabled Pass false to hold the request until it is actually needed. Callers that only care
 * once a dialog opens use this so every page render does not cost a round trip; the SWR key is
 * shared, so once fetched the settings tab and any gated caller read the same cache entry.
 */
export const useAtsConnectionStatus = (enabled: boolean = true) => {
    const companyId = useCompanyId();
    const { getAccessTokenSilently } = useAuth0();

    return useSWR(
        enabled ? atsConnectionPath(companyId) : null,
        async (path): Promise<AtsConnectionResponse | null> => {
            try {
                const response = await http.get<AtsConnectionResponse | null>({
                    path,
                    accessToken: await getAccessTokenSilently(),
                });

                // An empty body is read as "nothing connected", the same as a 404.
                return response || null;
            } catch (error) {
                // 404 means no ATS is connected yet - return null instead of throwing.
                if (error instanceof ResourceNotFoundError) {
                    return null;
                }
                throw error;
            }
        },
        {
            refreshInterval: (connection: AtsConnectionResponse | null | undefined) =>
                isAtsSyncFinished(connection) ? 0 : ATS_CONNECTION_POLL_INTERVAL_MS,
        },
    );
};

export const useCreateAtsConnectLink = () => {
    const companyId = useCompanyId();
    const { getAccessTokenSilently } = useAuth0();

    return useSWRMutation(
        `${atsConnectionPath(companyId)}/create-connection-link`,
        async (path: string, { arg }: { arg: string }) => {
            const response = await http.post<CreateAtsConnectLinkResponse>({
                path,
                accessToken: await getAccessTokenSilently(),
                body: { integrationTool: arg },
            });

            return response;
        },
    );
};

export const useActivateAtsConnection = () => {
    const companyId = useCompanyId();
    const { getAccessTokenSilently } = useAuth0();

    return useSWRMutation(
        `${atsConnectionPath(companyId)}/activate`,
        async (path: string, { arg }: { arg: string }) => {
            await http.post({
                path,
                accessToken: await getAccessTokenSilently(),
                body: { activationToken: arg },
            });
        },
    );
};

export const useDeleteAtsConnection = () => {
    const companyId = useCompanyId();
    const { getAccessTokenSilently } = useAuth0();

    // Keyed on the status path so a successful disconnect revalidates the connection status.
    return useSWRMutation(atsConnectionPath(companyId), async (path) => {
        await http.delete({
            path,
            accessToken: await getAccessTokenSilently(),
        });
    });
};
